/**
 * JWT decoding utilities for client-side token inspection.
 * Used to detect MFA requirements in auth tokens.
 */

export interface DecodedEnduserToken {
  id: string;
  type: "enduser";
  businessId: string;
  requiresMFA?: boolean;
  passwordIsUnset?: boolean;
  fromPublicSession?: boolean;
  sessionScopes?: string[];
  exp: number;
  iat: number;
}

/**
 * Decode a JWT token without verification.
 * This is safe for client-side inspection of token claims.
 */
export const decodeJwt = (token: string): DecodedEnduserToken | null => {
  try {
    const base64Payload = token.split(".")[1];
    if (!base64Payload) return null;

    const payload = atob(base64Payload);
    return JSON.parse(payload);
  } catch {
    return null;
  }
};

/**
 * Check if a token requires MFA verification.
 * Returns true if the token has the requiresMFA flag set.
 */
export const tokenRequiresMFA = (token: string): boolean => {
  const decoded = decodeJwt(token);
  return decoded?.requiresMFA === true;
};

/**
 * Check if the enduser associated with a token has no password set.
 * Returns true if the token has the passwordIsUnset flag set.
 */
export const tokenPasswordIsUnset = (token: string): boolean => {
  const decoded = decodeJwt(token);
  return decoded?.passwordIsUnset === true;
};

export const tokenIsFromPublicSession = (token: string): boolean => {
  const decoded = decodeJwt(token);
  return decoded?.fromPublicSession === true;
};

/**
 * Check if a token is a staff "View portal as patient" preview session.
 * These sessions are read-only — every write (including terms consent) 403s.
 */
export const tokenIsPortalPreview = (token: string): boolean => {
  const decoded = decodeJwt(token);
  return decoded?.sessionScopes?.includes("portal-preview") === true;
};
